import React, { useMemo, useState } from 'react';
import Menu, { TriggerProps } from '../../shared/Menu/Menu';
import MenuItem from '../../shared/Menu/MenuItem';
import excelIcon from '../../../static/icons/documentsType/excel.svg';
import Button from '../../shared/SmallComponents/Button';
import ArrayToExcelModal from '../../shared/ArrayToExcel/ArrayToExcelModal';
import { getExportConfigFromColumns } from '../../shared/ArrayToExcel/getExportConfigFromColumns';
import { ModalTriggerProps } from '../../shared/Modal/Modal';
import { pickBy } from 'lodash';
import Tooltip from '@mui/material/Tooltip';
import { ColumnType } from '../../shared/Grid/types/Column';
import { IFollowUpExtended } from './FollowUp';

export type FollowUpExportMenuProps = {
    /** Grid column definitions – export fields and labels are derived from these */
    columns: ColumnType<IFollowUpExtended>[];
    /** Data to export (e.g. active + completed follow-ups) */
    data: IFollowUpExtended[];
    /** Filename for the downloaded Excel file (without extension) */
    fileName?: string;
    /** Title shown in the export modal */
    title?: string;
    /** Tooltip when export is available */
    exportTooltip?: string;
    /** Tooltip when there is no data to export */
    noDataTooltip?: string;
};

const DEFAULT_FILE_NAME = 'followups';
const DEFAULT_MODAL_TITLE = 'Export Follow-ups Excel';

const FollowUpExportMenu = ({
    columns,
    data,
    fileName = DEFAULT_FILE_NAME,
    title = DEFAULT_MODAL_TITLE,
    exportTooltip = 'Export',
    noDataTooltip = 'No follow-ups to export'
}: FollowUpExportMenuProps) => {
    const [isModalOpened, setIsModalOpened] = useState(false);

    const { exportableFields, localization } = useMemo(() => {
        if (data.length === 0) {
            return { exportableFields: [] as string[], localization: {} };
        }
        return getExportConfigFromColumns<IFollowUpExtended>(columns, data);
    }, [columns, data]);

    const exportData = useMemo(() => {
        if (exportableFields.length === 0) return [];
        return data.map((row) =>
            pickBy(row, (_, key) => exportableFields.includes(key as string))
        ) as Array<Record<string, unknown>>;
    }, [data, exportableFields]);

    const hasData = exportData.length > 0 && exportableFields.length > 0;

    const menuItems = hasData
        ? [
              <ArrayToExcelModal<Record<string, unknown>>
                  key="followup-excel-export"
                  localization={localization}
                  data={exportData}
                  title={title}
                  fileName={fileName}
                  onCancelCallback={() => setIsModalOpened(false)}
                  Trigger={(props: ModalTriggerProps) => (
                      <MenuItem
                          onClick={(e) => {
                              e.stopPropagation();
                              setIsModalOpened(true);
                              props.onClick();
                          }}
                          icon={excelIcon}
                          text="Excel"
                      />
                  )}
              />
          ]
        : [];

    return (
        <Menu
            position="bottom-center"
            arrowOnTrigger={false}
            options={menuItems}
            isModalOpened={isModalOpened}
            MenuTrigger={(props: TriggerProps) => (
                <Button
                    width="32px"
                    height="32px"
                    borderRadius="50%"
                    margin="7px"
                    buttonType="hollow"
                    onClick={props.onClick}
                    disabled={!hasData}
                >
                    <Tooltip title={hasData ? exportTooltip : noDataTooltip} aria-label="export">
                        <span className="material-icons-outlined">file_download</span>
                    </Tooltip>
                </Button>
            )}
        />
    );
};

export default FollowUpExportMenu;
